import type { Dose } from '../data/types';
import { DOSE_LABELS, DOSE_ORDER } from '../data/types';
import styles from './Dial.module.css';

interface DialProps {
  dose: Dose;
  score: number;
}

/**
 * A vertical brass scale. Four marked positions, one for each dose level.
 * A needle slides along the scale to indicate the current recommendation.
 */
export function Dial({ dose, score }: DialProps) {
  const positions = DOSE_ORDER;
  const activeIndex = positions.indexOf(dose);
  // Normalize the score to a 0..1 range for needle position. Map roughly
  // 0..50 to the scale.
  const normalized = Math.max(0, Math.min(1, score / 50));
  // Invert because the dial has bedroom at bottom and tpe at top.
  const needleTop = `${(1 - normalized) * 100}%`;

  return (
    <div className={styles.frame} role="meter" aria-valuetext={DOSE_LABELS[dose]} aria-valuenow={Math.round(score)} aria-valuemin={0} aria-valuemax={50}>
      <div className={styles.scale}>
        {/* tick labels, top → bottom = tpe → bedroom */}
        {[...positions].reverse().map((p, idx) => (
          <div
            key={p}
            className={`${styles.tick} ${
              p === dose ? styles.tickActive : ''
            }`}
            style={{ top: `${(idx / (positions.length - 1)) * 100}%` }}
          >
            <span className={styles.tickMark} aria-hidden="true">
              —
            </span>
            <span className={styles.tickLabel}>{DOSE_LABELS[p]}</span>
          </div>
        ))}

        {/* the needle */}
        <div
          className={styles.needle}
          style={{ top: needleTop }}
          aria-hidden="true"
        >
          <span className={styles.needleHead} />
        </div>
      </div>

      <div className={styles.readout}>
        <p className={styles.label}>calibrator reads</p>
        <p className={styles.dose}>{DOSE_LABELS[dose]}</p>
        <p className={styles.position}>
          position {activeIndex + 1} of {positions.length}
        </p>
      </div>
    </div>
  );
}
